import { Anchor, Container, Text } from "@mantine/core";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getOrigin } from "src/environment";
import Coordinates from "src/generator/Coordinates/Coordinates";
import {
  boolnaData,
  cidrillsData,
  gmdssOptsData,
  ozonetypeData,
  povData,
  pressureConverter,
  secData,
  secstatusData,
  temperatureConverter,
} from "src/mock/selectOptions";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { useCanEditEntry } from "../hooks/useCanEditEntry";
import { useCellSavedState } from "../hooks/useCellSavedState";

import BloodPressure from "../_Medical/BloodPressure/BloodPressure";
import BodyPartInput from "../_Medical/BodyPart/BodyPartInput";
import BoolAttachText from "../_Medical/BoolAttachText/BoolAttachText";
import MedBasicInput from "../_Medical/MedBasicInput";
import TextAreaList from "../_Medical/TextAreaList/TextAreaList";
import Bioareas from "../_Selects/Bioareas/Bioareas";
import CabinSelect from "../_Selects/Cabins/CabinSelect";
import CustomSelect from "../_Selects/CustomSelect/CustomSelect";
import CustomMultiSelect from "../_Selects/CustomSelect/CustomMultiSelect";
import ShipType from "../_Selects/ShipTypeSelect/ShipTypeSelect";
import UserSelect from "../_Selects/UserSelect/UserSelect";
import ClickInput from "../_YachtDataInputs/ClickPopulateInput/ClickInput";
import DpsEquipInput from "../_YachtDataInputs/DpsEquipInput/DpsEquipInput";
import YachtDataPopulate from "../_YachtDataInputs/YachtDataPopulate/YachtDataPopulate";
import CaseReportInput from "../CaseReportInput/CaseReportInput";
import CheckListCell from "../CheckListCell/CheckListCell";
import UnitConverter from "../Converter/UnitConverter";
import OilCoordinates from "../Coordinates/OilCoordinates";
import CustomInput from "../CustomInput/CustomInput";
import CustomNumberInput from "../CustomNumberInput/CustomNumberInput";
import DatePicker from "../DatePicker/DatePicker";
import DefaultCheckbox from "../DefaultCheckbox/DefaultCheckbox";
import DualTimeInput from "../DualTimeInput/DualTimeInput";
import ORBTankSelect from "../DynamicSelects/ORBTankSelect";
import TankSelect from "../DynamicSelects/TankSelect";
import LiftingEquipmentSelect from "../DynamicSelects/LiftingEquipmentSelect";
import ExclusiveCheckbox from "../ExclusiveCheckbox/ExclusiveCheckbox";
import IlInput from "../ilInput/IlInput";
import ImageInput from "../ImageInput/ImageInput";
import LockTimeInput from "../LockTimeInput/LockTimeInput";
import SigboxInput from "../SigboxInput/SigboxInput";
import Signature from "../Signature/Signature";
import SignatureCell from "../SignatureCell/SignatureCell";
import SteeringPumps from "../SteeringPumps/SteeringPumps";
import Sub from "../Sub/Sub";
import TimeInputWithTimeZone from "../TimeInputWithTimeZone/TimeInputWithTimeZone";
import Week from "../Week/Week";
import TextAutocomplete from "../TextAutocomplete/TextAutocomplete";
import IomParticulars from "../IomParticulars/IomParticulars";
import VitalsInput from "../_Medical/Vitals/VitalsInputs/VitalsInput";
import VitalsList from "../_Medical/VitalsList/VitalsList";
import { IBATITem, ICell } from "./Cell.types";
import useGreyRowCheck from "./useGreyRowCheck";
import useNACellByClass from "./useNACellByClass";
import { OVERLAY_PREF } from "../constants";
import { getCellWidth } from "../utils/getCellWidth";
import MedAttachedInput from "../_Medical/MedAttachedInput/MedAttachedInput";
import GuernseyChildInput from "../../components/shared/GuernseyMulti/GuernseyChildInput";
import GuernseyMultiInput from "../GuernseyMulti/GuernseyMultiInput";
import SelectAutocomplete from "../SelectAutocomplete/SelectAutocomplete";
import SearchList from "../SearchList/SearchList";
import TriangleInput from "../TriangleInput/TriangleInput";
import LocLookup from "../LocLookup/LocLookup";
import DateOrTextInput from "../DateOrTextInput/DateOrTextInput";
import {
  buildRadioSelectorData,
  findRadioSiblingUuid,
} from "../utils/gmdssHelpers";
import { resolveCopyValTarget } from "../hooks/useCopyVal";
import type { ComboboxItem } from "@mantine/core";

const Cell = ({
  unitType,
  uuid,
  rowUuid,
  rowIndex,
  colIndex,
  cellId,
  cols,
  flex,
  list,
  tag,
  style,
  direction,
  showBothTimeTypes = false,
  totalRows,
  ...cell
}: ICell) => {
  const initialRender = useRef(true);
  const dispatch = useDispatch();
  const { setValue, getFieldState } = useFormContext();
  const fieldName = cell.exclusive
    ? `${uuid}-exclusive-${cell.exclusive}`
    : uuid;
  const data = useWatch({
    name: fieldName,
  });
  const cabins = useSelector(state => state.generatorSlice.cabins);
  const yachtData = useSelector(state => state.generatorSlice.yachtData);
  const unCodeEnabled = useSelector(
    state => state.generatorSlice.lbentry.unCodeEnabled,
  );

  const canEditEntry = useCanEditEntry();
  const { markRowAsUnsaved } = useCellSavedState(
    typeof cell.sectionIndex === "number" ? cell.sectionIndex : 0,
    typeof rowIndex === "number" ? rowIndex : 0,
  );

  // Get section context for vertical tabbing
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);
  const currentSection =
    typeof cell.sectionIndex === "number" && sections[cell.sectionIndex]
      ? sections[cell.sectionIndex].shared &&
        typeof cell.sharedIndex === "number" &&
        sections[cell.sectionIndex].sections
        ? sections[cell.sectionIndex].sections?.[cell.sharedIndex]
        : sections[cell.sectionIndex]
      : undefined;
  let currentCols: Record<string, any> = {};
  if (currentSection) {
    const regRow = currentSection.rows?.[rowIndex ?? 0];
    if (regRow?.cols) {
      currentCols = regRow.cols;
    } else if (regRow?.subrows) {
      // Row uses subrows — find the subrow that contains this cell's uuid
      const bareUuid = uuid.startsWith(OVERLAY_PREF)
        ? uuid.slice(OVERLAY_PREF.length)
        : uuid;
      const matchingSubrow = (regRow.subrows as any[]).find(
        sub => sub.cols && (bareUuid in sub.cols || uuid in sub.cols),
      );
      if (matchingSubrow?.cols) {
        currentCols = matchingSubrow.cols;
      }
    } else if (
      currentSection.subSections &&
      typeof cell.subSectionIndex === "number"
    ) {
      const sub = currentSection.subSections[cell.subSectionIndex] as any;
      const subRow = sub?.rows?.[rowIndex ?? 0];

      if (subRow?.items && rowUuid) {
        const items = subRow.items;
        currentCols = items[rowUuid].cols;
      }
    }
  }
  const numberOfCols = Object.values(currentCols).filter(
    (col: any) => col && !["BLANK", "space"].includes(col.unit),
  )?.length;

  const shouldGreyOut = useGreyRowCheck(cell.class, currentCols);
  const isNACell = useNACellByClass(cell.NA, currentCols);

  const isEqual = (v1: any, v2: any) => {
    if (typeof v1 !== typeof v2) return false;
    if (typeof v1 === "object") {
      return JSON.stringify(v1) === JSON.stringify(v2);
    } else {
      return v1 === v2;
    }
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (canEditEntry && !list) {
      if (!isEqual(data, cell.value)) {
        markRowAsUnsaved();

        const activeElement = document.activeElement as HTMLElement | null;
        const isFocusedField =
          activeElement?.id === uuid ||
          activeElement?.getAttribute("name") === fieldName;
        const isDirtyField = getFieldState(fieldName).isDirty;

        if (isFocusedField || isDirtyField) {
          dispatch(setEditing(true));
        }
      }
    }
  }, [
    canEditEntry,
    data,
    dispatch,
    fieldName,
    getFieldState,
    list,
    markRowAsUnsaved,
    unitType,
    cell.value,
    uuid,
  ]);

  const sectionTag = currentSection?.feature;
  const unit = unitType?.includes("vitals") ? "vitals" : unitType;

  const getInputByUnitType = () => {
    if (isNACell) {
      return (
        <Text
          c="grayText"
          id={uuid}
          style={{
            textAlign: "center",
            minWidth: getCellWidth(cell.width),
            display: "block",
          }}>
          N/A
        </Text>
      );
    }
    switch (unit) {
      case "text":
        if (cell.searchEntries) return <TextAutocomplete {...cell} id={uuid} />;
        // if (cell.insertedTankName !== undefined) return null;

        if (cell.il) {
          return <IlInput {...cell} id={uuid} tag={tag} rowIndex={rowIndex} />;
        } else if (
          tag &&
          ["med_fname", "med_sname", "med_dob", "med_position"].includes(tag)
        ) {
          return <MedBasicInput uuid={uuid} tag={tag} value={cell.value} />;
        } else {
          return (
            <CustomInput
              {...cell}
              tag={tag}
              id={uuid}
              domId={cell.id ?? ""}
              rowIndex={rowIndex}
              colIndex={colIndex}
              readonly={tag?.startsWith("OnyxPercTotal") ? true : cell.readonly}
              sectionIndex={cell.sectionIndex}
              numberOfCols={numberOfCols}
              totalRows={totalRows}
            />
          );
        }
      case "dateOrText":
        return (
          <DateOrTextInput
            {...cell}
            uuid={uuid}
            tag={tag}
            rowIndex={rowIndex}
            colIndex={colIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
            cell={cell}
            unitType={unitType}
          />
        );
      case "IOM_particulars":
        return (
          <IomParticulars id={uuid} value={cell.value} tag={tag as string} />
        );
      case "vitals":
        return (
          <VitalsInput
            unit={unitType as string}
            id={uuid}
            value={cell.value}
            suffix={cell.suffix}
          />
        );
      case "vital_list":
        return (
          <VitalsList
            {...cell}
            tag={tag as string}
            list={list as string[]}
            dynamicRows
            id={uuid}
          />
        );
      case "num":
      case "number":
        if (cell.name === "Official number") {
          return (
            <YachtDataPopulate
              id={uuid}
              unit="num"
              value={cell.value}
              required={cell.required}
            />
          );
        }
        return (
          <CustomNumberInput
            {...cell}
            sectionTag={sectionTag}
            id={uuid}
            rowIdx={rowIndex}
            tag={tag}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );
      case "locport":
      case "loclookup":
        if (unCodeEnabled) {
          return (
            <LocLookup
              {...cell}
              rowIndex={rowIndex}
              colIndex={colIndex}
              id={uuid}
              rowUuid={rowUuid}
              subUuid={cell.subUuid}
              cols={currentCols}
            />
          );
        } else {
          return (
            <CustomInput
              {...cell}
              id={uuid}
              sectionIndex={cell.sectionIndex}
              numberOfCols={numberOfCols}
              totalRows={totalRows}
            />
          );
        }
      case "loccountry":
        return (
          <CustomInput
            {...cell}
            id={cellId || uuid}
            sectionIndex={cell.sectionIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );
      case "large/text":
        if (list) {
          return (
            <TextAreaList
              {...cell}
              tag={tag as string}
              list={list as string[]}
              id={uuid}
            />
          );
        }

        if (cell.searchEntries)
          return (
            <TextAutocomplete
              {...cell}
              id={uuid}
              textarea={unit === "large/text"}
            />
          );

        return (
          <CustomInput
            {...cell}
            id={uuid}
            textarea={true}
            sectionIndex={cell.sectionIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );

      case "med_complaint":
        return <BodyPartInput {...cell} id={uuid} />;
      case "check":
        if (cell.exclusive) {
          return <ExclusiveCheckbox {...cell} id={uuid} tag={tag} />;
        } else {
          return (
            <DefaultCheckbox {...cell} id={uuid} rowIdx={rowIndex} tag={tag} />
          );
        }
      case "date": {
        const copyValTarget = resolveCopyValTarget(
          cell.feature,
          cell.target,
          currentCols,
        );
        return (
          <DatePicker
            {...cell}
            rowIndex={rowIndex}
            tag={tag}
            externalValue={cell.value}
            id={uuid}
            cellId={cellId}
            isFirstRow={rowIndex === 0}
            isShared={cell.isShared}
            copyValTarget={copyValTarget}
          />
        );
      }
      case "24lock":
        return <LockTimeInput {...cell} id={uuid} idx={rowIndex} data={data} />;
      case "time":
        // Retain 24lock for older entries as they still contain the unit, so this now defaults
        // to time instead of the old lock.
        if (cell.isOverlay) return null;
        if (showBothTimeTypes) {
          return (
            <DualTimeInput
              {...cell}
              id={uuid}
              idx={rowIndex}
              rowIndex={rowIndex}
              domId={cell.id ?? ""}
            />
          );
        }
        return (
          <TimeInputWithTimeZone
            {...cell}
            id={uuid}
            idx={rowIndex}
            rowIndex={rowIndex}
            domId={cell.id ?? ""}
            isLTorUTCOnly={true}
            tag={tag}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );
      case "case/report":
        return <CaseReportInput {...cell} id={uuid} />;
      case "lat":
        return (
          <OilCoordinates // with direction
            {...cell}
            id={uuid}
            isLatitude={true}
            isLongitude={false}
          />
        );
      case "lat4":
        return (
          <OilCoordinates // with direction
            {...cell}
            id={uuid}
            isLatitude={true}
            isLongitude={false}
            twoDigit={true}
          />
        );
      case "lat2":
        return (
          <Coordinates
            {...cell}
            id={uuid}
            isLatitude={true}
            isLongitude={false}
          />
        );
      case "lat3":
        return (
          <Coordinates
            {...cell}
            id={uuid}
            isLatitude={true}
            isLongitude={false}
            isLat3={true}
          />
        );
      case "long":
        return (
          <OilCoordinates // with direction
            {...cell}
            id={uuid}
            isLatitude={false}
            isLongitude={true}
          />
        );
      case "long4":
        return (
          <OilCoordinates // with direction
            {...cell}
            id={uuid}
            isLatitude={false}
            isLongitude={true}
            twoDigit={true}
          />
        );
      case "long3":
        return (
          <Coordinates
            {...cell}
            id={uuid}
            isLatitude={false}
            isLongitude={true}
            isLong3={true}
          />
        );
      case "long2":
        return (
          <Coordinates
            id={uuid}
            value={cell.value}
            isLatitude={false}
            isLongitude={true}
            required={cell.required}
          />
        );
      case "latCustom":
        return (
          <Coordinates
            id={uuid}
            value={cell.value}
            isLatitude={true}
            isLongitude={false}
            required={cell.required}
            config={cell.config}
          />
        );
      case "longCustom":
        return (
          <Coordinates
            id={uuid}
            value={cell.value}
            isLatitude={false}
            isLongitude={true}
            required={cell.required}
            config={cell.config}
          />
        );
      case "triangle":
        return <TriangleInput {...cell} id={uuid} />;
      case "temperature":
        return (
          <UnitConverter
            {...cell}
            id={uuid}
            converterName={"Temperature converter"}
            selectOptionsData={temperatureConverter}
            rowIndex={rowIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );
      case "pressure":
        return (
          <UnitConverter
            {...cell}
            id={uuid}
            converterName={"Pressure converter"}
            selectOptionsData={pressureConverter}
            rowIndex={rowIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );

      /* -------------------------------------------------------------------------------------------------------/
       * WARNING / DO NOT MODIFY THIS CODE WITHOUT CHECKING, TO ENSURE ITS CORRECT
       -------------------------------------------------------------------------------------------------------/
       * FOR USER COMPONENTS AND LISTS
       *
       * This ensures that if there is a user already set, that user is guaranteed to be shown regardless
       * of the users active/inactive/archived/deleted this is a NON NEGOTIABLE and required for legal
       * purposes if you need to ever change this, you must ensure a saved user is shown on Logbooks, Summary,
       * Reports regardless of the users active/inactive/archived/deleted status.
       * END WARNING --------------------------------------------------------------------------------- */
      case "user":
      case "userref":
      case "userrank":
      case "master":
      case "Captain/Master":
      case "sig":
      case "officer":
      case "engineers":
      case "userrefpop":
      case "userrefpopiom":
      case "userrefpopname":
      case "users":
        const copyValTarget = resolveCopyValTarget(
          cell.feature,
          cell.target,
          currentCols,
        );
        return (
          <UserSelect
            {...cell}
            id={uuid}
            cellCase={unitType}
            tag={tag}
            extra={cell.extra}
            rowIndex={rowIndex}
            copyValTarget={copyValTarget}
          />
        );
      case "medOfficer":
        const copyValTargetMed = resolveCopyValTarget(
          cell.feature,
          cell.target,
          currentCols,
        );
        return (
          <UserSelect
            {...cell}
            id={uuid}
            cellCase={unitType}
            unit={unit}
            extra={cell.extra}
            copyValTarget={copyValTargetMed}
            // autopopulateId="medSig"
          />
        );

      case "signature":
        return <Signature {...cell} uuid={uuid} unitType={unitType} />;
      case "bloodpressure":
        return <BloodPressure {...cell} uuid={uuid} />;
      case "link":
        return (
          <Container p={0} m={0} maw="100%" style={{ textAlign: "center" }}>
            <Anchor
              href={
                cell.href && cell.href.indexOf("http") > -1
                  ? cell.href
                  : getOrigin() + "/" + cell.href
              }
              target="_blank"
              align="center">
              {cell.value || cell.hname}
            </Anchor>
          </Container>
        );
      case "sigbox":
        return (
          <SigboxInput
            {...cell}
            feature={cell.feature ?? ""}
            unit={unitType}
            id={uuid}
            cellCase={unitType}
          />
        );
      case "BLANK":
        if (cellId === "signature") {
          return <SignatureCell id={uuid} />;
        } else {
          return (
            <>
              <Text
                id={uuid}
                fz="inherit"
                fw={cell.bold ? "bold" : "normal"}
                style={{ textAlign: (cell.align as any) ?? "left" }}>
                {cell.name}
              </Text>
              {cell.small && (
                <Text mt="0.25rem" size="xs">
                  {cell.small}
                </Text>
              )}
            </>
          );
        }
      case "yachtname":
      case "port":
      case "sign":
      case "imo":
      case "gross":
      case "net":
      case "ofnum":
      case "mmsi":
      case "len":
      case "flag":
      case "dnl":
      case "owner":
      case "sbm":
      case "vesseltype":
      case "lblang":
      case "dp_equip":
      case "dp_equipclass":
      case "bio_csoc":
      case "bio_cnot":
      case "bio_byard":
      case "bio_hullnum":
      case "bio_keel":
      case "bio_deldate":
      case "bio_lenloa":
      case "bio_lenlbp":
      case "bio_bmob":
      case "bio_dmodd":
      case "bio_ddmo":
      case "bio_sdmo": {
        let unit: string = unitType;
        if (unit === "ofnum") unit = "num";
        else if (unit === "vesseltype") unit = "vtype";
        else if (unit.includes("dp_")) unit = `dynpos.${unit.slice(3)}`;
        else if (unit.includes("bio_")) unit = `biofoul.${unit.slice(4)}`;
        else if (unit === "owner")
          unit = `gmdss.${unit}`; // GMDSS
        else if (unit === "sbm") unit = `gmdss.${unit}`; // GMDSS
        return <YachtDataPopulate {...cell} id={uuid} unit={unit} />;
      }
      case "bioareas":
        return <Bioareas {...cell} id={uuid} />;
      case "dps_equip":
        const sectionData = {
          dynamic: currentSection?.dynamicSection,
          className: currentSection?.className,
        };
        return (
          <DpsEquipInput
            {...cell}
            id={uuid}
            rowIndex={rowIndex ?? -1}
            sectionData={sectionData}
          />
        );
      case "batser1":
      case "batser2":
      case "batser3":
      case "batexp1":
      case "batexp2":
      case "batexp3": {
        return <ClickInput {...cell} id={uuid} unit={unitType} />;
      }
      case "shiptype":
        return <ShipType {...cell} id={uuid} />;
      case "sub":
        // const hasLatLong = cols
        //   ? Object.entries(cols).some(
        //       ([key, value]: [string, ICol]) =>
        //         value?.unit?.startsWith("lat") ||
        //         value?.unit?.startsWith("long"),
        //     )
        //   : false;

        return (
          <Sub
            subUuid={uuid}
            cols={cols}
            flex={flex}
            direction={direction}
            rowIndex={rowIndex}
            required={cell.required}
            sectionIndex={cell.sectionIndex}
            // totalRows={totalRows}
            // numberOfCols={numberOfCols}
          />
        );
      case "m3":
        return (
          <CustomNumberInput
            {...cell}
            id={uuid}
            suffix={
              <span>
                m<sup>3</sup>
              </span>
            }
          />
        );
      case "cabins":
        return <CabinSelect {...cell} id={uuid} data={cabins} />;
      case "cidrills":
        return (
          <CustomSelect {...cell} id={uuid} miw={300} data={cidrillsData} />
        );
      case "gmdss-opts":
        return <CustomSelect {...cell} id={uuid} data={gmdssOptsData} />;
      case "radioSelector": {
        const gmdss = (yachtData as any)?.gmdss ?? {};
        const gmdssKey =
          cell.radioType === "sart" ? "sat" : (cell.radioType ?? "");
        const items: Array<{ serial: string; exp: string }> =
          gmdss[gmdssKey] ?? [];
        const isExp = cell.exp === true;
        const selectData = buildRadioSelectorData(items, isExp);
        const siblingUuid = findRadioSiblingUuid(
          currentSection?.rows,
          rowIndex ?? 0,
          colIndex ?? 0,
          cell.radioType ?? "",
          isExp,
        );
        const handleRadioChange = (selected: string) => {
          if (!siblingUuid) return;
          const matched = items.find(item =>
            isExp ? item.exp === selected : item.serial === selected,
          );
          if (matched) {
            setValue(siblingUuid, isExp ? matched.serial : matched.exp);
          }
        };
        return (
          <CustomSelect
            {...cell}
            id={uuid}
            data={selectData}
            onChange={handleRadioChange}
          />
        );
      }
      case "pov":
        return <CustomSelect {...cell} id={uuid} data={povData} />;
      case "boolna":
        return <CustomSelect {...cell} id={uuid} data={boolnaData} />;
      case "secstatus":
        return <CustomSelect {...cell} id={uuid} data={secstatusData} />;
      case "sec":
        return (
          <CustomSelect
            {...cell}
            id={uuid}
            data={secData}
            insertedValue={cell.insertedValue}
          />
        );
      case "oilrec1":
        return <ORBTankSelect {...cell} id={uuid} unit="oilrec1" />;
      case "oilrec2":
        return <ORBTankSelect {...cell} id={uuid} unit="oilrec2" />;
      case "tanks":
        // @ts-expect-error type is mandatory in TankSelect
        return <TankSelect {...cell} id={uuid} />;
      case "ozonetype":
        return <CustomSelect {...cell} id={uuid} data={ozonetypeData} />;
      case "liftingEquip": {
        return (
          <LiftingEquipmentSelect
            id={uuid}
            value={cell.value}
            required={cell.required}
            sectionIndex={cell.sectionIndex}
            rowIndex={rowIndex}
            rowUuid={rowUuid}
            sharedIndex={cell.sharedIndex}
            subSectionIndex={cell.subSectionIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
            feature={cell.feature}
          />
        );
      }
      case "searchlist":
      case "multilist":
      case "list": {
        const mappedData: ComboboxItem[] = [];
        if (list)
          list.forEach((el: any) => {
            if (typeof el === "string") {
              mappedData.push({ label: el, value: el });
            } else {
              mappedData.push({
                label: el?.value,
                value: el?.key,
                ...(el?.label && { isLabel: el?.label }),
              });
            }
          });

        if (unit === "searchlist") {
          return (
            <SearchList
              {...cell}
              id={uuid}
              data={mappedData}
              tag={tag}
              rowIndex={rowIndex}
              colIndex={colIndex}
            />
          );
        }

        if (unit === "multilist") {
          return (
            <CustomMultiSelect
              {...cell}
              id={uuid}
              data={mappedData}
              tag={tag}
              rowIndex={rowIndex}
              colIndex={colIndex}
            />
          );
        }

        if ((cell as any).ambGetPreviousData === "seclevel") {
          // Special handling for security level with ambGetPreviousData
          return (
            <SelectAutocomplete
              {...cell}
              id={uuid}
              data={mappedData}
              tag={tag}
              rowIndex={rowIndex}
              colIndex={colIndex}
            />
          );
        }

        if (cell.allowText && cell.searchEntries) {
          return <SelectAutocomplete {...cell} id={uuid} data={mappedData} />;
        }

        return (
          <CustomSelect
            {...cell}
            id={uuid}
            data={mappedData}
            tag={tag}
            rowIndex={rowIndex}
            colIndex={colIndex}
            sectionIndex={cell.sectionIndex}
            numberOfCols={numberOfCols}
            totalRows={totalRows}
          />
        );
      }
      case "week":
        return <Week id={uuid} {...cell} />;
      case "bool/attach/text":
        return (
          <BoolAttachText
            {...cell}
            tag={tag as string}
            list={list as IBATITem[]}
            id={uuid}
          />
        );
      case "guernseyMulti":
        return (
          <GuernseyMultiInput
            content={cell.content}
            value={cell.value}
            id={uuid}
            rowIndex={rowIndex}
          />
        );
      case "guernsey-child":
        return <GuernseyChildInput tag={tag} rowIndex={rowIndex} />;
      case "medAttached":
        return <MedAttachedInput {...cell} id={uuid} />;
      case "steering.pumps":
        return <SteeringPumps {...cell} id={uuid} key={cell.id} />;
      case "checklist":
        return <CheckListCell {...cell} id={uuid} />;
      case "attach":
        return <ImageInput {...cell} id={uuid} />;
      default:
        if (cell.searchEntries) return <TextAutocomplete {...cell} id={uuid} />;

        if (cell.il) {
          return (
            <IlInput
              {...cell}
              tag={tag}
              id={uuid}
              orCode={cellId}
              rowIndex={rowIndex}
            />
          );
        } else if (cols) {
          return (
            <Sub
              subUuid={uuid}
              cols={cols}
              direction={direction}
              flex={flex}
              rowIndex={rowIndex}
              required={cell.required}
              value={cell.value}
              sectionIndex={cell.sectionIndex}
            />
          );
        } else {
          return (
            <CustomInput
              {...cell}
              tag={tag}
              id={uuid}
              domId={cell.id ?? ""}
              rowIndex={rowIndex}
              colIndex={colIndex}
              readonly={tag?.startsWith("OnyxPercTotal") ? true : cell.readonly}
              sectionIndex={cell.sectionIndex}
              numberOfCols={numberOfCols}
              totalRows={totalRows}
            />
          );
        }
    }
  };

  return (
    <div
      id={cellId}
      style={{
        ...style,
        ...(shouldGreyOut ? { opacity: 0.5, pointerEvents: "none" } : {}),
      }}>
      {getInputByUnitType()}
    </div>
  );
};

export default Cell;
